import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, ChevronDown, Home, Image, Tag, AlertTriangle, Phone, Info, Wrench, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContactSettings } from "@/hooks/useContactSettings";
const SmartSearchDialog = lazy(() => import("@/components/SmartSearchDialog"));

const mainLinks = [
  { label: "الرئيسية", path: "/", icon: Home },
  { label: "معرض الأعمال", path: "/معرض-الأعمال", icon: Image },
  { label: "العروض", path: "/العروض", icon: Tag },
  { label: "الطوارئ", path: "/طوارئ", icon: AlertTriangle },
  { label: "تواصل معنا", path: "/تواصل-معنا", icon: Phone },
  { label: "من نحن", path: "/من-نحن", icon: Info },
  { label: "خدماتنا", path: "/خدماتنا", icon: Wrench },
];

const moreLinks = [
  { label: "طلب تصميم", path: "/طلب-تصميم" },
  { label: "حجز موعد", path: "/حجز-موعد" },
  { label: "تتبع الطلب", path: "/تتبع-الطلب" },
  { label: "المدونة", path: "/المدونة" },
  { label: "مقارنة المنتجات", path: "/مقارنة" },
  { label: "تصميم ذكي AI", path: "/تصميم-ذكي" },
  { label: "خريطة المشاريع", path: "/خريطة-المشاريع" },
  { label: "برنامج الإحالة", path: "/الإحالة" },
  { label: "برنامج الولاء", path: "/برنامج-الولاء" },
];

const allLinks = [...mainLinks, ...moreLinks.map(l => ({ ...l, icon: undefined }))];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { getWhatsAppLink } = useContactSettings();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!moreOpen) return;
    const handleClick = () => setMoreOpen(false);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [moreOpen]);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  // Load saved theme on mount
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved === "dark") {
      setIsDark(true);
      document.documentElement.classList.add("dark");
    }
  }, []);

  return (
    <nav className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/95 backdrop-blur-xl shadow-md border-b border-border" : "bg-background/80 backdrop-blur-lg border-b border-border/50"}`}>
      {/* Official badge */}
      <div className="bg-gradient-woody text-primary-foreground text-center text-[11px] py-1 font-medium tracking-wide">
        ✦ الموقع الرسمي والوحيد لمجمع الشويع الصناعي — حيدان، صعدة ✦
      </div>

      <div className="container mx-auto flex items-center justify-between h-14 md:h-16">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-lg bg-gradient-woody flex items-center justify-center shadow-sm group-hover:shadow-woody transition-shadow">
            <span className="text-primary-foreground font-bold text-base">ش</span>
          </div>
          <span className="font-bold text-base hidden sm:block text-foreground group-hover:text-primary transition-colors">مجمع الشويع</span>
        </Link>

        <div className="hidden lg:flex items-center gap-0.5">
          {mainLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-3 py-2 rounded-lg text-[13px] font-semibold transition-all duration-200 ${
                location.pathname === link.path
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-foreground/80 hover:bg-primary/10 hover:text-primary"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-[13px] font-semibold transition-all duration-200 ${moreOpen ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/10 hover:text-primary"}`}
            >
              المزيد <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div className="absolute top-full left-0 mt-2 bg-card border border-border rounded-xl shadow-xl py-2 min-w-[200px] z-50 animate-fade-in">
                {moreLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMoreOpen(false)}
                    className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                      location.pathname === link.path
                        ? "bg-primary/10 text-primary"
                        : "text-foreground/80 hover:bg-muted hover:text-primary"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="text-foreground/80 hover:text-primary h-9 w-9" title="بحث ذكي (Ctrl+K)" aria-label="فتح البحث الذكي">
            <Search className="h-4.5 w-4.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleDark} className="text-foreground/80 hover:text-primary h-9 w-9" aria-label={isDark ? "تفعيل الوضع الفاتح" : "تفعيل الوضع الداكن"}>
            {isDark ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
          </Button>
          <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="hidden lg:inline-flex">
            <Button size="sm" className="bg-gradient-woody text-primary-foreground hover:opacity-90 shadow-sm text-[13px] px-5">
              <Phone className="h-3.5 w-3.5 ml-1.5" />
              اتصل بنا
            </Button>
          </a>
          <Button variant="ghost" size="icon" className="lg:hidden text-foreground h-9 w-9" onClick={() => setIsOpen(!isOpen)} aria-label={isOpen ? "إغلاق القائمة" : "فتح القائمة"} aria-expanded={isOpen}>
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="lg:hidden bg-background border-t border-border animate-fade-in max-h-[80vh] overflow-y-auto">
          <div className="container py-3 flex flex-col gap-1">
            {allLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all flex items-center gap-3 ${
                  location.pathname === link.path
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-foreground/80 hover:bg-primary/10 hover:text-primary"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-border my-2 pt-2">
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-gradient-woody text-primary-foreground shadow-sm">
                  <Phone className="h-4 w-4 ml-2" />
                  تواصل عبر واتساب
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        <SmartSearchDialog isOpen={searchOpen} onClose={() => setSearchOpen(prev => !prev)} />
      </Suspense>
    </nav>
  );
}
